import React, {useState} from "react";
import {
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Icon,
    Input,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Portal,
    Skeleton,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import {flexRender, getCoreRowModel, useReactTable} from "@tanstack/react-table";
import axios from "axios";
import Card from "components/card/Card";
import {MdMoreHoriz, MdOutlineDelete, MdOutlineEdit, MdVpnKey} from "react-icons/md";

export default function ColumnsTablePermission({columnsData, tableData, onDataUpdate, isLoading: isTableLoading}) {
    const {isOpen, onOpen, onClose} = useDisclosure();
    const {isOpen: isOpenDelete, onOpen: onOpenDelete, onClose: onCloseDelete} = useDisclosure();
    const [selectedRow, setSelectedRow] = useState(null);
    const [editData, setEditData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const toast = useToast();

    const textColor = useColorModeValue("gray.700", "gray.200");
    const headerTextColor = useColorModeValue("gray.600", "gray.200");
    const borderColor = useColorModeValue("gray.200", "gray.600");
    const headerBgColor = useColorModeValue("gray.50", "gray.700");
    const tableBodyBg = useColorModeValue("white", "gray.800");
    const mutedTextColor = useColorModeValue("secondaryGray.600", "secondaryGray.500");
    const rowHoverBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
    const modalBg = useColorModeValue("white", "navy.800");
    const fieldBg = useColorModeValue("white", "navy.700");

    const table = useReactTable({data: tableData || [], columns: columnsData, getCoreRowModel: getCoreRowModel()});

    const handleEditClick = (rowData) => {
        setSelectedRow(rowData);
        setEditData({service: rowData.service || "", menu: rowData.menu || "", subMenu: rowData.subMenu || "", action: rowData.action || ""});
        onOpen();
    };

    const handleDeleteClick = (rowData) => {
        setSelectedRow(rowData);
        onOpenDelete();
    };

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setEditData(prev => ({...prev, [name]: value}));
    };

    const token = document.cookie.split(";").find((c) => c.trim().startsWith("token=")).split("=")[1];
    const handleUpdate = async () => {
        setIsLoading(true);
        try {
            const requestBody = {id: selectedRow.id, service: editData.service, menu: editData.menu, subMenu: editData.subMenu, action: editData.action};
            const response = await axios.patch(process.env.REACT_APP_API_URL + `/v1/permission`, requestBody, {
                headers: {Authorization: `Bearer ${token}`}
            });
            if (response.status === 200) {
                toast({title: "Update successful", description: "Data has been updated successfully", status: "success", duration: 3000, isClosable: true, position: "top-right"});
                onDataUpdate();
                onClose();
            }
        } catch (error) {
            toast({title: "Update failed", description: error.response?.data?.message || "An error occurred", status: "error", duration: 3000, isClosable: true, position: "top-right"});
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        setIsLoading(true);
        try {
            const requestBody = {nrp: selectedRow.nrp, email: selectedRow.email};
            const response = await axios.delete(process.env.REACT_APP_API_URL + `/v1/users/delete`, {
                data: requestBody,
                headers: {Authorization: `Bearer ${token}`}
            });

            if (response.status === 200) {
                toast({title: "Delete successful", description: "Data has been deleted successfully", status: "success", duration: 3000, isClosable: true, position: "top-right"});
                onDataUpdate();
                onCloseDelete();
            }
        } catch (error) {
            toast({title: "Delete failed", description: error.response?.data?.message || "An error occurred", status: "error", duration: 3000, isClosable: true, position: "top-right"});
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Card p="0" mb="24px" overflow="hidden">
                <Flex px={{base: 4, md: 6}} py={5} justify="space-between" align="center" borderBottomWidth="1px" borderColor={borderColor} gap={4}>
                    <Box><Text color={textColor} fontSize="lg" fontWeight="700">Daftar Permission</Text><Text color={mutedTextColor} fontSize="sm" mt={1}>Pilih menu aksi untuk mengelola data permission.</Text></Box>
                    <Text color={mutedTextColor} fontSize="sm" fontWeight="600" whiteSpace="nowrap">{isTableLoading ? "Memuat data..." : `${table.getRowModel().rows.length} permission`}</Text>
                </Flex>
                <Box overflowX="auto">
                    <Table variant="simple" bg={tableBodyBg} minW="1080px">
                        <Thead>{table.getHeaderGroups().map((headerGroup) => <Tr key={headerGroup.id}>{headerGroup.headers.map((header) => <Th key={header.id} color={headerTextColor} py={4} fontSize="xs" letterSpacing="0.06em" textTransform="uppercase" bg={headerBgColor} borderColor={borderColor}>{flexRender(header.column.columnDef.header, header.getContext())}</Th>)}<Th color={headerTextColor} py={4} fontSize="xs" letterSpacing="0.06em" textTransform="uppercase" bg={headerBgColor} borderColor={borderColor} textAlign="right">Actions</Th></Tr>)}</Thead>
                        <Tbody>
                            {isTableLoading ? (
                                Array.from({length: 5}).map((_, rowIndex) => <Tr key={rowIndex}>{Array.from({length: columnsData.length + 1}).map((__, cellIndex) => <Td key={cellIndex} py={4} borderColor={borderColor}><Skeleton height="16px" borderRadius="full"/></Td>)}</Tr>)
                            ) : table.getRowModel().rows.length > 0 ? (
                                table.getRowModel().rows.map((row) => <Tr key={row.id} transition="background 0.2s ease" _hover={{bg: rowHoverBg}}>{row.getVisibleCells().map((cell) => <Td key={cell.id} py={4} color={textColor} fontSize="sm" borderColor={borderColor}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Td>)}<Td py={3} borderColor={borderColor} textAlign="right"><Menu placement="bottom-end"><MenuButton as={Button} variant="light" size="sm" aria-label={`Aksi untuk ${row.original.service}`} rightIcon={<Icon as={MdMoreHoriz} boxSize={5}/>}>Aksi</MenuButton><Portal><MenuList minW="190px" p={2}><MenuItem borderRadius="8px" icon={<Icon as={MdOutlineEdit} boxSize={4}/>} onClick={() => handleEditClick(row.original)}>Edit permission</MenuItem><MenuItem borderRadius="8px" color="red.500" icon={<Icon as={MdOutlineDelete} boxSize={4}/>} onClick={() => handleDeleteClick(row.original)}>Hapus permission</MenuItem></MenuList></Portal></Menu></Td></Tr>)
                            ) : (
                                <Tr><Td colSpan={columnsData.length + 1} borderColor={borderColor}><Flex direction="column" align="center" justify="center" py={12} gap={3}><Flex w="48px" h="48px" borderRadius="full" bg={rowHoverBg} align="center" justify="center"><Icon as={MdVpnKey} boxSize={6} color="brand.500"/></Flex><Box textAlign="center"><Text color={textColor} fontWeight="700">Permission tidak ditemukan</Text><Text color={mutedTextColor} fontSize="sm" mt={1}>Ubah atau reset filter untuk melihat data permission.</Text></Box></Flex></Td></Tr>
                            )}
                        </Tbody>
                    </Table>
                </Box>
            </Card>

            <Modal isOpen={isOpen} onClose={onClose} isCentered>
                <ModalOverlay/>
                <ModalContent bg={modalBg} borderRadius="20px">
                    <ModalHeader borderBottomWidth="1px" borderColor={borderColor}>Edit Data</ModalHeader>
                    <ModalCloseButton/>
                    <ModalBody py={6}>
                        <FormControl mb={4}><FormLabel>Name Service</FormLabel><Input name="service" value={editData.service} onChange={handleInputChange} bg={fieldBg}/></FormControl>
                        <FormControl mb={4}><FormLabel>Menu</FormLabel><Input name="menu" value={editData.menu} onChange={handleInputChange} bg={fieldBg}/></FormControl>
                        <FormControl mb={4}><FormLabel>Sub Menu</FormLabel><Input name="subMenu" value={editData.subMenu} onChange={handleInputChange} bg={fieldBg}/></FormControl>
                        <FormControl><FormLabel>Action</FormLabel><Input name="action" value={editData.action} onChange={handleInputChange} bg={fieldBg}/></FormControl>
                    </ModalBody>
                    <ModalFooter borderTopWidth="1px" borderColor={borderColor}>
                        <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                        <Button colorScheme="blue" onClick={() => {
                            onClose();
                            let Swal = require('sweetalert2');
                            Swal.fire({
                                title: 'Are you sure?',
                                text: "Please confirm to save this data",
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonColor: '#3085d6',
                                cancelButtonColor: '#d33',
                                confirmButtonText: 'Yes, save it!'
                            }).then((result) => {
                                if (result.isConfirmed) {
                                    handleUpdate();
                                } else {
                                    onOpen();
                                }
                            });
                        }} isLoading={isLoading}>Save Changes</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            <Modal isOpen={isOpenDelete} onClose={onCloseDelete} isCentered>
                <ModalOverlay/>
                <ModalContent bg={modalBg} borderRadius="20px">
                    <ModalHeader borderBottomWidth="1px" borderColor={borderColor}>Delete Data</ModalHeader>
                    <ModalCloseButton/>
                    <ModalBody py={6}><Text color={textColor}>Are you sure want to delete data with NRP: <Text as="span" fontWeight="700">{selectedRow?.nrp}</Text>?</Text></ModalBody>
                    <ModalFooter borderTopWidth="1px" borderColor={borderColor}><Button variant="ghost" mr={3} onClick={onCloseDelete}>Cancel</Button><Button colorScheme="red" onClick={handleDelete} isLoading={isLoading}>Delete</Button></ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}
